import {
  Box,
  Center,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Spacer,
  Stack,
  useDisclosure,
  useMediaQuery,
} from '@chakra-ui/react';
import { OrderDateRange } from '@diamond/shared/types';
import {
  Button,
  Divider,
  Dropdown,
  IconButton,
  Modal,
  RangeDatePicker,
  Text,
} from '@diamond/shared/ui';
import {
  statusData,
  unixTimeFromDaysAgo,
  unixToHumanDate,
} from '@diamond/shared/utils';
import { useDivisionData } from '@diamond/sol-buyer-context';
import {
  DateRangeOutlined,
  ExpandMoreOutlined,
  FilterAltOutlined,
  SearchOutlined,
} from '@mui/icons-material';
import {
  endOfDay,
  fromUnixTime,
  getUnixTime,
  parse,
  startOfDay,
} from 'date-fns';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useFormContext } from 'react-hook-form';
import { useDebounce } from 'usehooks-ts';

import { GetMyOrderFiltersReturnType } from './filters';

export interface FilterFormProps {
  buttonFilterLabel: string;
  filters: NonNullable<GetMyOrderFiltersReturnType>;
}

const STATUS_LIST = [
  {
    value: '',
    state: 'all',
    label: 'Semua Pesanan',
  },
  {
    value: 'unpaid',
    state: 'unpaid',
    label: 'Pesanan Belum Dibayar',
  },
  {
    value: 'ongoing_order',
    state: 'ongoing_order',
    label: 'Pesanan Sedang Berlangsung',
  },
  {
    value: 'order_history',
    state: 'order_history',
    label: 'Riwayat Pesanan',
  },
];

const DATE_RANGES: Record<Exclude<OrderDateRange, 'custom'>, string> = {
  all: 'Pilih Tanggal Order',
  '7d': '7 Hari Terakhir',
  '1m': '1 Bulan Terakhir',
  '3m': '3 Bulan Terakhir',
  '6m': '6 Bulan Terakhir',
  '1y': '1 Tahun Terakhir',
};

export function MyOrderHeader({ buttonFilterLabel, filters }: FilterFormProps) {
  const router = useRouter();

  const { start_date: valStartDate, end_date: valEndDate } = filters;

  const { register, setValue, watch } = useFormContext();
  const { divisionData } = useDivisionData();
  const searchQuery = useDebounce<string>(watch('search'), 500);

  const [labelFilterDate, setLabelFilterDate] = useState(() => {
    const date_range = filters.date_range ?? '';

    if (valStartDate > 0 || valEndDate > 0) {
      const labelStartDate =
        valStartDate && valStartDate > 0
          ? unixToHumanDate(valStartDate)
          : undefined;

      const labelEndDate =
        valEndDate && valEndDate > 0 ? unixToHumanDate(valEndDate) : undefined;

      if (date_range && date_range !== 'all' && date_range !== 'custom') {
        return DATE_RANGES[date_range];
      }

      if (labelStartDate !== undefined || labelEndDate !== undefined) {
        return `${labelStartDate} - ${labelEndDate}`;
      }
    }

    return 'Pilih Tanggal Order';
  });

  const [isMobile] = useMediaQuery('(max-width: 768px)');

  const date = useDisclosure();
  const filter = useDisclosure();

  const [isFilterDateReset, setIsFilterDateReset] = useState<boolean>(false);

  const selectedDateFilter: DateRange = {
    from: valStartDate ? fromUnixTime(valStartDate) : undefined,
    to: valEndDate ? fromUnixTime(valEndDate) : undefined,
  };

  const selectedDateRangeState = useState<DateRange | undefined>(
    selectedDateFilter
  );
  const setSelectedDateRange = selectedDateRangeState[1];

  useEffect(() => {
    if (searchQuery !== router.query['search']) {
      const url = {
        pathname: router.pathname,
        query: {
          ...router.query,
          search: searchQuery,
        },
      };
      router.push(url, undefined, { shallow: true });
    }
  }, [router, searchQuery]);

  const resetDateRange = () => {
    setValue('order_date-from', '');
    setValue('order_date-to', '');
    setSelectedDateRange(undefined);
  };

  const handleSubmitFilterDate = () => {
    const startDate = isFilterDateReset
      ? undefined
      : getUnixTime(
          startOfDay(parse(watch('order_date-from'), 'dd/MM/yyyy', new Date()))
        );
    const endDate = isFilterDateReset
      ? undefined
      : getUnixTime(
          endOfDay(parse(watch('order_date-to'), 'dd/MM/yyyy', new Date()))
        );

    if (isFilterDateReset) {
      resetDateRange();
      setLabelFilterDate('Pilih Tanggal Order');
    }

    const labelStartDate =
      startDate && startDate > 0 ? unixToHumanDate(startDate) : undefined;
    const labelEndDate =
      endDate && endDate > 0 ? unixToHumanDate(endDate) : undefined;
    const finalFilterLabel =
      labelStartDate || labelEndDate
        ? `${labelStartDate} - ${labelEndDate}`
        : 'Pilih Tanggal Order';

    setLabelFilterDate(finalFilterLabel);

    setIsFilterDateReset(false);

    const url = {
      pathname: router.pathname,
      query: {
        ...router.query,
        date_range: 'custom',
        start_date: startDate,
        end_date: endDate,
        page: 1,
      },
    };
    router.push(url, undefined, { shallow: true });

    date.onClose();
  };

  const handleSubmitFilter = () => {
    const url = {
      pathname: router.pathname,
      query: {
        ...router.query,
        status: watch('status'),
        divisi_code: watch('divisi_code'),
        page: 1,
      },
    };
    router.push(url, undefined, { shallow: true });
  };

  const handleHapusFilter = () => {
    setValue('status', '');
    setValue('divisi_code', '');

    const url = {
      pathname: router.pathname,
      query: {
        ...router.query,
        status: undefined,
        divisi_code: undefined,
        page: 1,
      },
    };

    router.push(url, undefined, { shallow: true });
  };

  const handleFilterTanggal = (status: string) => {
    const today = Number((new Date().getTime() / 1000).toFixed(0));
    let dateFrom;
    let dateEnd;
    switch (status) {
      case 'all':
        dateFrom = undefined;
        dateEnd = undefined;
        setLabelFilterDate('Pilih Tanggal Order');
        break;
      case '7d':
        dateFrom = unixTimeFromDaysAgo(7);
        dateEnd = today;
        setLabelFilterDate('7 Hari Terakhir');
        break;
      case '1m':
        dateFrom = unixTimeFromDaysAgo(30);
        dateEnd = today;
        setLabelFilterDate('1 Bulan Terakhir');
        break;
      case '3m':
        dateFrom = unixTimeFromDaysAgo(90);
        dateEnd = today;
        setLabelFilterDate('3 Bulan Terakhir');
        break;
      case '6m':
        dateFrom = unixTimeFromDaysAgo(180);
        dateEnd = today;
        setLabelFilterDate('6 Bulan Terakhir');
        break;
      case '1y':
        dateFrom = unixTimeFromDaysAgo(365);
        dateEnd = today;
        setLabelFilterDate('1 Tahun Terakhir');
        break;
      default:
        break;
    }

    const url = {
      pathname: router.pathname,
      query: {
        ...router.query,
        start_date: dateFrom,
        end_date: dateEnd,
        date_range: status,
        page: 1,
      },
    };
    router.push(url, undefined, { shallow: true });

    resetDateRange();
  };

  const handleResetFilterDate = () => {
    resetDateRange();
    setIsFilterDateReset(true);
  };

  const handleCloseFilterDate = () => {
    if (isFilterDateReset === true) {
      setIsFilterDateReset(false);
      setValue(
        'order_date-from',
        valStartDate > 0 ? unixToHumanDate(valStartDate, 'dd/MM/yyyy') : ''
      );
      setValue(
        'order_date-to',
        valEndDate > 0 ? unixToHumanDate(valEndDate, 'dd/MM/yyyy') : ''
      );
      setSelectedDateRange({
        from: valStartDate > 0 ? new Date(valStartDate * 1000) : undefined,
        to: valEndDate > 0 ? new Date(valEndDate * 1000) : undefined,
      });
    }
    date.onClose();
  };

  return (
    <Box flex="1">
      <Divider my={5} />
      <Flex w="full" my="4" alignItems="center" gap="2">
        <Box display="flex">
          <Menu>
            {isMobile ? (
              <MenuButton
                as={IconButton}
                icon={<DateRangeOutlined />}
                alignContent="center"
                variant="outline"
                name="filter-date-mobile"
              />
            ) : (
              <MenuButton
                as={Button}
                leftIcon={<DateRangeOutlined />}
                rightIcon={<ExpandMoreOutlined />}
                alignContent="center"
                name="filter-date-desktop"
              >
                {labelFilterDate}
              </MenuButton>
            )}
            <MenuList zIndex={{ base: 1000, md: '1' }}>
              <MenuItem onClick={() => handleFilterTanggal('all')}>
                Semua
              </MenuItem>
              <MenuItem onClick={() => handleFilterTanggal('7d')}>
                7 Hari Terakhir
              </MenuItem>
              <MenuItem onClick={() => handleFilterTanggal('1m')}>
                1 Bulan Terakhir
              </MenuItem>
              <MenuItem onClick={() => handleFilterTanggal('3m')}>
                3 Bulan Terakhir
              </MenuItem>
              <MenuItem onClick={() => handleFilterTanggal('6m')}>
                6 Bulan Terakhir
              </MenuItem>
              <MenuItem onClick={() => handleFilterTanggal('1y')}>
                1 Tahun Terakhir
              </MenuItem>
              <MenuItem onClick={date.onOpen}>Pilih Tanggal</MenuItem>
            </MenuList>
          </Menu>
        </Box>
        <Box w={{ base: 'full', md: '528px' }}>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <SearchOutlined />
            </InputLeftElement>
            <Input
              type="search"
              placeholder="Cari Pesanan"
              bgColor="white"
              autoComplete="off"
              aria-label="search"
              {...register('search')}
            />
          </InputGroup>
        </Box>
        <Spacer />
        <Box ml={{ base: '-2', md: 'unset' }}>
          <Button
            name="filter-button"
            variant="outline"
            rightIcon={<FilterAltOutlined />}
            fontSize="14px"
            onClick={filter.onOpen}
          >
            {buttonFilterLabel}
          </Button>
        </Box>
      </Flex>
      <Divider />
      <Box overflow="auto">
        <Stack
          w="full"
          spacing="4"
          direction="row"
          py="6"
          align="center"
          overflowX="auto"
        >
          <Text
            fontWeight="bold"
            fontSize="lg"
            mr="6"
            display={{ base: 'none', md: 'flex' }}
          >
            Status
          </Text>
          {STATUS_LIST.map(({ label, value }) => (
            <Button
              key={label}
              minW="max-content"
              onClick={() => {
                setValue('group_status', value);
                const url = {
                  pathname: router.pathname,
                  query: {
                    ...router.query,
                    group_status: value,
                    page: 1,
                  },
                };
                router.push(url, undefined, { shallow: true });
              }}
              isActive={watch('group_status') === value}
              _active={{
                bg: 'rgba(0, 0, 163, 0.1)',
                color: 'blue',
                borderColor: 'blue',
              }}
            >
              {label}
            </Button>
          ))}
        </Stack>
      </Box>

      <Modal
        size="2xl"
        title="Pilih Tanggal"
        name="date_order"
        isOpen={date.isOpen}
        closeOnOverlayClick={false}
        onClose={handleCloseFilterDate}
        onSubmit={handleSubmitFilterDate}
      >
        <Center>
          <Stack>
            <Flex>
              <Spacer />
              <Button
                color="red"
                variant="link"
                border="none"
                fontWeight="normal"
                onClick={handleResetFilterDate}
              >
                Hapus
              </Button>
            </Flex>
            <RangeDatePicker
              key={`${selectedDateRangeState[0]?.from}${selectedDateRangeState[0]?.to}`}
              name="order_date"
              mode="range"
              selectedDateRangeState={selectedDateRangeState}
            />
          </Stack>
        </Center>
      </Modal>
      <Modal
        name="filter_order"
        isOpen={filter.isOpen}
        onClose={filter.onClose}
        onSubmit={() => {
          handleSubmitFilter();
          filter.onClose();
        }}
        size="xl"
      >
        <Box mt="6">
          <Flex align="center">
            <Text fontSize="xl" fontWeight="bold">
              Filter Status Order
            </Text>
            <Spacer />
            <Button variant="link" color="red" onClick={handleHapusFilter}>
              Hapus Filter
            </Button>
          </Flex>
          <Divider mt={5} display={{ base: 'none', md: 'flex' }} />
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={{ base: '5', md: '10' }}
            my="4"
          >
            <Dropdown
              name="divisi_code"
              label="Divisi"
              placeholder="Semua"
              size="md"
              items={divisionData}
              register={register}
            />
            <Dropdown
              name="status"
              label="Status"
              placeholder="Semua"
              size="md"
              items={statusData}
              register={register}
            />
          </SimpleGrid>
          <Divider display={{ base: 'none', md: 'flex' }} />
        </Box>
      </Modal>
    </Box>
  );
}
